/**
 * The entry screen and the spine of the application.
 *
 * Server-paged, server-sorted, server-searched. The client passes `q`, `sort`, `dir`, `page`,
 * `researchStatus` and `incomplete`, and renders what comes back. It never filters a page it was
 * given, because the page is a page of a larger answer.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppShell, PageHeader, Toolbar } from '~/layout/AppShell';
import { useScreen } from '~/layout/RouteAnnouncer';
import { QueryError } from '~/layout/ErrorBoundary';
import { DataTable, type Column } from '~/ui/DataTable';
import { Button } from '~/ui/Button';
import { SearchInput, Select } from '~/ui/Field';
import { EmptyState, Callout } from '~/ui/Feedback';
import { Legend, Swatch, SkeletonStack } from '~/ui/Surface';
import { Pill } from '~/ui/Pill';
import { useJurisdictions, useMeta } from '~/api/queries';
import { useUrlState } from '~/hooks/useUrlState';
import { useTableSort } from '~/hooks/useTableSort';
import { routes } from '~/lib/routes';
import { fmtCount, plural } from '~/lib/format';
import { RegisterNameCell, lineageFacts } from './RegisterName';
import { MatterBarGroup } from './RuleBars';
import { GapCell } from './GapCell';
import { StatusPill, ResearchStatusBadge } from './pills';
import { QueueActionButton } from '../queue/QueueActionButton';
import { TemporalBanner } from '../matter/TemporalBanner';
import type { JurisdictionRow } from '~/api/schema';
import styles from './JurisdictionsPage.module.css';

const SORT_KEYS = ['name', 'code', 'status', 'rulecount', 'rules', 'chains', 'opened'] as const;

const SPEC = {
  q: { fallback: '' },
  sort: { fallback: 'name', allowed: SORT_KEYS as unknown as readonly string[] },
  dir: { fallback: 'asc', allowed: ['asc', 'desc'] },
  page: { fallback: '1' },
  researchStatus: {
    fallback: '',
    allowed: ['never_researched', 'pending_research', 'current'],
  },
  incomplete: { fallback: '', allowed: ['true'] },
} as const;

/** Descending-first on every numeric measure puts the registers needing work at the top. */
const DEFAULT_DIRECTION = { name: 'asc', code: 'asc' } as const;

export function JurisdictionsPage() {
  const headingRef = useScreen('Jurisdictions');
  const navigate = useNavigate();
  const meta = useMeta();
  const { values, set } = useUrlState(SPEC);

  const [typed, setTyped] = useState(values.q);

  // The search box replaces rather than pushes, or the back button walks the keystrokes.
  useEffect(() => {
    if (typed === values.q) return;
    const timer = setTimeout(() => set({ q: typed, page: '1' }, 'replace'), 250);
    return () => clearTimeout(timer);
  }, [typed, values.q, set]);

  const page = Number.parseInt(values.page, 10);
  const query = useJurisdictions({
    ...(values.q ? { q: values.q } : {}),
    sort: values.sort,
    dir: values.dir,
    page: Number.isNaN(page) || page < 1 ? 1 : page,
    ...(values.researchStatus ? { researchStatus: values.researchStatus } : {}),
    ...(values.incomplete ? { incomplete: values.incomplete } : {}),
  });

  const toggleSort = useTableSort(
    { key: values.sort, dir: values.dir as 'asc' | 'desc' },
    (next) => set({ sort: next.key, dir: next.dir, page: '1' }),
    DEFAULT_DIRECTION,
  );

  const matters = meta.data?.matters ?? [];

  const columns: ReadonlyArray<Column<JurisdictionRow>> = [
    {
      key: 'name',
      label: 'Jurisdiction',
      sortable: true,
      width: '30%',
      render: (row) => (
        <RegisterNameCell
          code={row.code}
          name={row.name}
          registry={row.registry}
          incomplete={row.incomplete}
          inactive={row.status === 'dead'}
          href={routes.overview(row.code)}
          facts={lineageFacts(row)}
          badges={
            <span className={styles.badges}>
              {row.incomplete ? <Pill tone="quest" size="mini">Incomplete</Pill> : null}
              <ResearchStatusBadge status={row.researchStatus} />
            </span>
          }
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '104px',
      render: (row) => <StatusPill status={row.status} />,
    },
    {
      key: 'rulecount',
      label: 'Rules',
      sortable: true,
      numeric: true,
      width: '70px',
      render: (row) => fmtCount(row.matters.reduce((sum, entry) => sum + entry.rules, 0)),
    },
    {
      key: 'rules',
      label: 'Versions',
      sortable: true,
      numeric: true,
      width: '86px',
      render: (row) => fmtCount(row.matters.reduce((sum, entry) => sum + entry.versions, 0)),
    },
    {
      key: 'chains',
      label: 'Rule history',
      sortable: true,
      width: '232px',
      render: (row) => <MatterBarGroup row={row} matters={matters} interactive />,
    },
    {
      key: 'gaps',
      label: 'Gap analysis',
      width: '190px',
      render: (row) => <GapCell row={row} matters={matters} />,
    },
    {
      key: 'opened',
      label: 'Opened',
      sortable: true,
      numeric: true,
      width: '86px',
      render: (row) => fmtCount(row.founded),
    },
    {
      key: 'queue',
      label: 'Research',
      labelHidden: true,
      width: '172px',
      render: (row) => (
        <div className={styles.queueCell}>
          <QueueActionButton
            code={row.code}
            name={row.name}
            matters={row.defaultMatters}
            matterVocabulary={matters}
            openEntry={row.openQueueEntry}
            label="Queue research"
            stopPropagation
          />
        </div>
      ),
    },
  ];

  const data = query.data;
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? 25;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const filtered = values.q !== '' || values.researchStatus !== '' || values.incomplete !== '';

  return (
    <AppShell leaf="Jurisdictions" count={total}>
      <PageHeader
        headingRef={headingRef}
        title="Jurisdictions"
        description="Every register the engine models. A register that has been renamed or closed stays in the list, because rights filed under it still fall due."
      />

      {/*
        A confined lawyer sees only the registers they are assigned (ADR-0015). Saying so is the
        whole reason `everyRegister` is on the payload: an unexplained short list reads as data
        missing rather than as a boundary doing its job. The filtering is the server's — this only
        narrates it.
      */}
      {meta.data && !meta.data.principal.everyRegister ? (
        <Callout tone="warn" title="You are seeing the registers you are assigned">
          An administrator decides which registers you work on. If one you expect is missing, ask
          them to assign it rather than looking for it here.
        </Callout>
      ) : null}

      {data ? <TemporalBanner asAt={data.asAt} recordedAt={data.recordedAt} historical={data.historical} /> : null}

      <Toolbar>
        <label className="visually-hidden" htmlFor="jurisdiction-search">
          Search registers
        </label>
        <SearchInput
          id="jurisdiction-search"
          value={typed}
          onChange={setTyped}
          placeholder="Search by name, code, registry or a former name"
        />
        <div className={styles.filters}>
          <label className={styles.filterLabel} htmlFor="research-filter">
            Research
          </label>
          <Select
            id="research-filter"
            value={values.researchStatus}
            onChange={(next) => set({ researchStatus: next, page: '1' })}
            options={[
              { value: '', label: 'Any status' },
              { value: 'never_researched', label: 'Never researched' },
              { value: 'pending_research', label: 'Pending research' },
              { value: 'current', label: 'Current' },
            ]}
          />
          <label style={{ display: 'inline-flex', gap: 7, alignItems: 'center', fontSize: 12.5 }}>
            <input
              type="checkbox"
              checked={values.incomplete === 'true'}
              onChange={(event) =>
                set({ incomplete: event.currentTarget.checked ? 'true' : '', page: '1' })
              }
            />
            Succession stubs only
          </label>
        </div>
        {data ? (
          <span className={styles.summary}>
            {plural(data.totals.versions ?? 0, 'rule text')}, {data.totals.inForce ?? 0} in force
          </span>
        ) : null}
      </Toolbar>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending || !data ? (
        <SkeletonStack rows={8} height={64} />
      ) : total === 0 && !filtered ? (
        <EmptyState
          actions={
            <Button variant="primary" onClick={() => void navigate(routes.adminRegisters())}>
              Add the first register
            </Button>
          }
        >
          <div className={styles.firstRun}>
            <h2>There are no registers yet</h2>
            <p>
              A register is an office whose law this engine models — a national registry, a
              regional one like EUIPO or ARIPO, or an international system like the Madrid
              Protocol. It is not a country: registers outlive and outlast states.
            </p>
            <p>
              A register that has closed is kept rather than deleted, because rights filed under it
              are still running and still fall due. The same is true of one that has been renamed:
              a right is searched for under the name the register carried on its filing date, so
              every former name stays searchable.
            </p>
            <p>
              Nothing is seeded. Add the first register and the engine will start from what you
              tell it.
            </p>
          </div>
        </EmptyState>
      ) : total === 0 ? (
        <EmptyState>
          <p>
            No register matches that. Search covers the code, the name, the registry, the
            successors and <strong>both sides of every rename</strong> — so a register filed under
            a former name is found by that name.
          </p>
        </EmptyState>
      ) : (
        <>
          <DataTable
            caption="Registers the engine models"
            columns={columns}
            rows={data.jurisdictions}
            rowKey={(row) => row.code}
            rowInactive={(row) => row.status === 'dead'}
            sort={{ key: values.sort, dir: values.dir as 'asc' | 'desc' }}
            onSortChange={toggleSort}
          />
          <Legend
            aria-label="What the rule-history bars show"
            items={[
              { key: <Swatch tone="inforce" />, label: 'In force' },
              { key: <Swatch tone="superseded" />, label: 'Superseded' },
            ]}
          />
          <div className={styles.footer}>
            <span>
              Showing {data.jurisdictions.length} of {plural(total, 'jurisdiction')}
            </span>
            <span className={styles.pager}>
              <Button
                size="sm"
                inactive={page <= 1}
                onClick={() => set({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <span>
                Page {page} of {lastPage}
              </span>
              <Button
                size="sm"
                inactive={page >= lastPage}
                onClick={() => set({ page: String(page + 1) })}
              >
                Next
              </Button>
            </span>
          </div>
        </>
      )}

      {meta.isError ? (
        <Callout tone="issue" title="The engine could not be read" alert>
          The bootstrap payload failed to load, so matter labels and the rule catalogue are
          missing from this screen.
        </Callout>
      ) : null}

    </AppShell>
  );
}
